import { createElement, Volume2, VolumeX } from "lucide";

export interface AmbientAudioConfig {
  src: string;
  volume?: number;
  loop?: boolean;
}

const AUDIO_ID = "quiz-ambient-audio";
const TOGGLE_ID = "quiz-ambient-audio-toggle";

let audio: HTMLAudioElement | undefined;
let toggle: HTMLButtonElement | undefined;
let wantsPlayback = false;
let visibilityListenerAttached = false;
let eventHandler: ((event: "audio_start" | "audio_mute" | "audio_unmute") => void) | undefined;

export function prepareAmbientAudio(
  config?: AmbientAudioConfig,
  onEvent?: (event: "audio_start" | "audio_mute" | "audio_unmute") => void
): void {
  eventHandler = onEvent;
  if (!config) {
    destroyAmbientAudio();
    return;
  }

  if (audio?.dataset.src === config.src) return;
  destroyAmbientAudio();

  audio = document.createElement("audio");
  audio.id = AUDIO_ID;
  audio.dataset.src = config.src;
  audio.src = config.src;
  audio.preload = "auto";
  audio.loop = config.loop ?? true;
  audio.volume = clampVolume(config.volume ?? 0.18);
  audio.setAttribute("aria-hidden", "true");
  document.body.append(audio);

  toggle = document.createElement("button");
  toggle.id = TOGGLE_ID;
  toggle.className = "ambient-audio-toggle";
  toggle.type = "button";
  toggle.hidden = true;
  toggle.addEventListener("click", () => {
    if (!audio) return;
    audio.muted = !audio.muted;
    updateToggle();
    eventHandler?.(audio.muted ? "audio_mute" : "audio_unmute");
    if (!audio.muted && wantsPlayback && audio.paused) void audio.play().catch(() => undefined);
  });
  document.body.append(toggle);
  updateToggle();

  if (!visibilityListenerAttached) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    visibilityListenerAttached = true;
  }
}

export async function startAmbientAudio(): Promise<void> {
  if (!audio || !toggle) return;
  wantsPlayback = true;
  toggle.hidden = false;
  try {
    await audio.play();
    eventHandler?.("audio_start");
  } catch {
    audio.muted = true;
    updateToggle();
  }
}

function updateToggle(): void {
  if (!audio || !toggle) return;
  const muted = audio.muted;
  toggle.replaceChildren(createElement(muted ? VolumeX : Volume2, {
    width: 20,
    height: 20,
    "stroke-width": 1.9,
    "aria-hidden": "true"
  }));
  const label = muted ? "Увімкнути музику" : "Вимкнути музику";
  toggle.setAttribute("aria-label", label);
  toggle.title = label;
  toggle.dataset.muted = String(muted);
}

function handleVisibilityChange(): void {
  if (!audio || !wantsPlayback) return;
  if (document.hidden) audio.pause();
  else if (!audio.muted) void audio.play().catch(() => undefined);
}

function destroyAmbientAudio(): void {
  wantsPlayback = false;
  audio?.pause();
  audio?.remove();
  toggle?.remove();
  audio = undefined;
  toggle = undefined;
}

function clampVolume(value: number): number {
  return Math.min(1, Math.max(0, value));
}
